<template>
	<transition name="ui--audio">
		<player
			v-if="open"
			class="ui ui--audio"
			:player-state="playerState"
			:player-type="playerType"
			:media-loading="mediaLoading"
		>
			<ElementButton name="play" @click="play" i18n :content="playButtonContent" />
			<ElementMedia
				type="audio"
				:title="title"
				:description="description"
				:duration="duration"
				:pastTime="pastTime"
				:remainingTime="remainingTime"
			/>
			<ElementProgress
				:progress="progress"
				:min="0.0"
				:max="100"
				:value="Math.round((currentTime / duration) * 100)"
				:unit="$t('seconds')"
				:touched="touched"
				:duration="duration"
				:pastTime="pastTime"
				:remainingTime="remainingTime"
				ref="progress"
			/>
			<ElementButton name="close" @click="close" i18n content="close" />
		</player>
	</transition>
</template>
<script>
import { mapActions, mapMutations } from 'vuex';
import { mutationTypes, actionTypes } from '../../types';
import { PLAYER_TYPE, PLAYER_STATE } from '../../tools/consts';
import { mapStateNested } from '../../tools/helpers';
import { logger } from '../../tools';

export default {
	data() {
		return {
			mediaLoading: true,
		};
	},

	computed: {
		...mapStateNested('playerAudio', [
			'title',
			'description',
			'duration',
			'open',
			'playerState',
			'synced',
			'currentTime',
			'source',
			'setCurrentTime',
			'isSynced',
		]),
		remainingTime() {
			return this.duration - this.currentTime;
		},
		pastTime() {
			return this.currentTime;
		},
		progress() {
			return this.currentTime / this.duration;
		},
		playerType() {
			return this.synced ? PLAYER_TYPE.synced : PLAYER_TYPE.default;
		},
		playButtonContent() {
			return this.playerState === PLAYER_STATE.playing ? 'pause' : 'play';
		},
	},
	methods: {
		...mapActions([actionTypes.audioClose, actionTypes.audioTogglePlay]),
		...mapMutations([
			mutationTypes.audioSetState,
			mutationTypes.audioSetCurrentTime,
			mutationTypes.resetStartAt,
			mutationTypes.audioSetPlayer,
		]),
		play() {
			this.audioTogglePlay();
		},
		close() {
			this.audioClose();
		},
		touched(leftRight) {
			if (!this.isSynced) this.audio.currentTime = leftRight * this.duration;
		},
	},
	watch: {
		async source(newSource) {
			if (this.audio.src !== newSource) {
				const tmp = this.playerState;
				if (!this.isSynced) await this.audio.pause();
				this.audio.src = newSource;
				if (!this.isSynced) {
					await this.audio.load();
					if (tmp == PLAYER_STATE.playing) {
						setTimeout(async () => {
							try {
								await this.audio.play();
							} catch (error) {
								console.warn('audioPlayer play ', error);
							}
						}, 0);
					}

					if (typeof this.setCurrentTime === 'number') {
						const startTime = parseFloat(this.setCurrentTime);
						this.audio.currentTime = startTime;
						function canPlayHandler() {
							logger.log('Audio PlayHandler canplaythrough is working', startTime);
							this.currentTime = startTime;
							this.removeEventListener('canplaythrough', canPlayHandler);
						}
						this.audio.addEventListener('canplaythrough', canPlayHandler);
						this.audioSetCurrentTime({
							currentTime: this.audio.currentTime,
							clearSetCurrentTime: true,
						});
					}
				}
			}
		},
		async playerState(newState) {
			if (newState === PLAYER_STATE.playing) {
				this.audio.currentTime = this.currentTime;
				if (!this.isSynced) {
					try {
						await this.audio.play();
					} catch (error) {
						console.warn('audioPlayer play ', error);
					}
				}
			} else if (newState === PLAYER_STATE.paused) {
				if (!this.isSynced) {
					this.audio.pause();
				}
			} else if (newState === PLAYER_STATE.stopped) {
				if (!this.isSynced) {
					this.audio.currentTime = 0;
					await this.audio.pause();
				}
			}
			// else if (newState === PLAYER_STATE.loading) {
			// }
		},

		setCurrentTime(value) {
			if (typeof value === 'number' && !this.isSynced) {
				logger.log('Audio setCurretnTime changes 0!"!', value);
				this.audio.currentTime = Math.floor(parseFloat(value));
				this[mutationTypes.audioSetCurrentTime]({
					currentTime: this.audio.currentTime,
					clearSetCurrentTime: true,
				});
			}
		},
	},
	mounted() {
		// <audio ref="audio" controls :src="source"></audio>
		this.audio = new Audio();
		this.audioSetPlayer(this.audio);

		const UPDATE_INTERVALL = 30;
		let lastUpdate = 0;
		// Transform audio events in state audio manipulation
		this.audio.addEventListener('timeupdate', () => {
			if (Date.now() - lastUpdate > UPDATE_INTERVALL) {
				lastUpdate = Date.now();
				this[mutationTypes.audioSetCurrentTime]({ currentTime: this.audio.currentTime });
			}
		});
		this.audio.addEventListener('loadstart', () => {
			logger.log('Audio Event loadstart');
			this[mutationTypes.audioSetState]({ playerState: PLAYER_STATE.loading });
		});
		this.audio.addEventListener('stalled', () => {
			logger.log('Audio Event stalled');
			this[mutationTypes.audioSetState]({ playerState: PLAYER_STATE.loading });
		});
		// this.audio.addEventListener('canplay', () => {
		// 	logger.log('Audio Event canplay');
		// });

		this.audio.addEventListener('play', () => {
			logger.log('Audio Event play');
			this[mutationTypes.audioSetState]({ playerState: PLAYER_STATE.playing });
		});
		this.audio.addEventListener('playing', () => {
			if (!this.isSynced) {
				logger.log('Audio Event playing');
				this[mutationTypes.audioSetState]({ playerState: PLAYER_STATE.playing });
			}
		});

		this.audio.addEventListener('error', () => {
			logger.log('Audio Event error');
			this[mutationTypes.audioSetState]({ playerState: PLAYER_STATE.stopped });
		});
		this.audio.addEventListener('ended', () => {
			logger.log('Audio Event ended');
			if (!this.isSynced) {
				this[mutationTypes.audioSetState]({ playerState: PLAYER_STATE.ended });
				this.close();
			}
		});

		this.audio.addEventListener('pause', () => {
			logger.log('Audio Event pause');
			this[mutationTypes.audioSetState]({ playerState: PLAYER_STATE.paused });
		});

		// this.audio.addEventListener('canplaythrough', () => {
		// 	logger.log('Audio event canplaythrough');
		// 	// if (this.playerState === PLAYER_STATE.loading ||  PLAYER_STATE.playing ) {
		// 	// 	this.audio.play();
		// 	// }
		// 	//  this.resetStartAt();
		// 	// if (this.startAt) {
		// 	// 	//this.audio.currentTime = this.startAt;
		// 	// 	this[mutationTypes.audioSetCurrentTime]({ currentTime: this.audio.currentTime });
		// 	// 	console.log('currentTime', this.currentTime);
		// 	// 	this.resetStartAt();
		// 	// }
		// });

		this.audio.addEventListener('waiting', () => {
			this.mediaLoading = true;
		});
		this.audio.addEventListener('seeking', () => {
			this.mediaLoading = true;
		});
		this.audio.addEventListener('canplay', () => {
			this.mediaLoading = false;
		});
	},
};
</script>
